import React, { useState } from 'react';

import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { useDataProvider, useNotify } from 'react-admin';

import { Checkmark } from 'icons';
import DeclineDialog from './DeclineDialog';
import PreviewDialog from './PreviewDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 262,
    minHeight: 565,
    borderRadius: '16px',
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    position: 'relative',
    transition: theme.transitions.create(['box-shadow', 'border-color'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  selected: {
    boxShadow: theme.shadows[3],
  },
  media: {
    position: 'relative',
    flex: 1,
    width: '100%',
    backgroundColor: theme.palette.grey[100],
    marginBottom: '-20px',
  },
  mediaImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  mediaClickable: {
    cursor: 'pointer',
  },
  checkbox: {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(1.5),
    padding: '10px',
    backgroundColor: theme.palette.common.white,
    borderRadius: '50%',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.16)',
    transition: theme.transitions.create(['box-shadow', 'background-color', 'transform'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&.Mui-checked': {
      backgroundColor: theme.palette.common.white,
    },
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 1)',
      transform: 'translateY(-3px)',
    },
    '&.Mui-checked:hover': {
      backgroundColor: theme.palette.common.white,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 1)',
      transform: 'translateY(-3px)',
    },
  },
  checkboxIcon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'block',
  },
  checkboxUncheckedIcon: {
    color: theme.palette.text.secondary,
  },
  checkboxCheckedIcon: {
    backgroundColor: theme.palette.common.white,
  },
  footer: {
    backgroundColor: theme.palette.background.paper,
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxShadow: `0 -1px 0 ${theme.palette.grey[200]}`,
    zIndex: 1,
  },
  title: {
    fontWeight: 700,
    fontSize: 18,
    textAlign: 'center',
  },
  name: {
    fontWeight: 500,
    fontSize: 16,
    textAlign: 'center',
  },
  email: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    marginTop: theme.spacing(1),
  },
  actionButton: {
    width: 50,
    height: 50,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.common.white,
  },
  approve: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

type ItemCardProps = {
  id: string;
  title: string;
  name?: string;
  email?: string;
  screenshotUrl?: string;
  selected?: boolean;
  onSelectToggle?: () => void;
  status?: string;
  onStatusChange?: () => void;
};

const ItemCard = ({
  id,
  title,
  name,
  email,
  screenshotUrl,
  selected = false,
  onSelectToggle,
  status,
  onStatusChange,
}: ItemCardProps) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineSubmitting, setDeclineSubmitting] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const showActions = status === 'PENDING';
  const allowSelection = status === 'PENDING';

  const handleApprove = async () => {
    try {
      setApproveSubmitting(true);
      await dataProvider.post('rewardsUpdateVerificationStatus', {
        id,
        data: { status: 'APPROVED' },
      });
      onStatusChange?.();
    } catch (error: any) {
      const message =
        typeof error === 'string' ? error : error?.message || 'Failed to approve item';
      notify(message, 'warning');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleOpenDecline = () => setDeclineOpen(true);
  const handleCloseDecline = () => setDeclineOpen(false);

  const handleOpenLightbox = () => setLightboxOpen(true);
  const handleCloseLightbox = () => setLightboxOpen(false);

  const handleSubmitDecline = async ({ note }) => {
    try {
      setDeclineSubmitting(true);
      await dataProvider.post('rewardsUpdateVerificationStatus', {
        id,
        data: { status: 'REJECTED', note },
      });
      handleCloseDecline();
      onStatusChange?.();
    } catch (error: any) {
      const message =
        typeof error === 'string' ? error : error?.message || 'Failed to decline item';
      notify(message, 'warning');
    } finally {
      setDeclineSubmitting(false);
    }
  };

  return (
    <Box className={`${classes.root} ${selected ? classes.selected : ''}`}>
      <Box className={classes.media}>
        {allowSelection && (
          <Checkbox
            className={classes.checkbox}
            icon={
              <RadioButtonUncheckedIcon
                className={`${classes.checkboxIcon} ${classes.checkboxUncheckedIcon}`}
              />
            }
            checkedIcon={
              <Checkmark className={`${classes.checkboxIcon} ${classes.checkboxCheckedIcon}`} />
            }
            color="primary"
            checked={selected}
            onChange={onSelectToggle}
          />
        )}
        {screenshotUrl ? (
          <img
            src={screenshotUrl}
            alt={title}
            className={`${classes.mediaImg} ${classes.mediaClickable}`}
            onClick={handleOpenLightbox}
          />
        ) : null}
      </Box>

      <Box className={classes.footer}>
        <Typography className={classes.title}>{title}</Typography>
        {name ? <Typography className={classes.name}>{name}</Typography> : null}
        {email ? <Typography className={classes.email}>{email}</Typography> : null}
        {showActions && (
          <Box className={classes.actions}>
            <IconButton className={classes.actionButton} onClick={handleOpenDecline}>
              <CloseRoundedIcon />
            </IconButton>
            <IconButton
              className={`${classes.actionButton} ${classes.approve}`}
              onClick={handleApprove}
              disabled={approveSubmitting}>
              <CheckRoundedIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {declineOpen && (
        <DeclineDialog
          open={declineOpen}
          onClose={handleCloseDecline}
          onSubmit={handleSubmitDecline}
          submitting={declineSubmitting || approveSubmitting}
          title={title}
          name={name}
          email={email}
          screenshotUrl={screenshotUrl}
        />
      )}

      {lightboxOpen && screenshotUrl && (
        <PreviewDialog
          open={lightboxOpen}
          onClose={handleCloseLightbox}
          title={title}
          imageUrl={screenshotUrl}
        />
      )}
    </Box>
  );
};

export default ItemCard;
